/**
 * M6 Marketing Demographics Matrix
 *
 * Renders the Wallace v2 M6 per-Assessment-Area table: one row per AA, with
 * six race/ethnicity columns (Hispanic, American Indian, Asian, Black, Native
 * Hawaiian, White). Each cell shows population share vs application share and
 * colors under-representation red, over-representation green, and equality
 * (or zero population) black.
 */

import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

export type M6Group =
  | 'HISPANIC'
  | 'AMERICAN_INDIAN'
  | 'ASIAN'
  | 'BLACK'
  | 'NATIVE_HAWAIIAN'
  | 'WHITE';

interface M6GroupCell {
  group: M6Group;
  populationCount: number;
  populationPct: number;
  applicationCount: number;
  applicationPct: number;
  disparity: number; // applicationPct - populationPct
  representationRatio: number | null;
  underrepresented: boolean;
}

interface M6AssessmentAreaRow {
  assessmentAreaName: string;
  totalPopulation: number;
  totalApplications: number;
  groups: M6GroupCell[];
}

interface M6MatrixProps {
  rows: M6AssessmentAreaRow[];
  /** HMDA year for the applications band heading. */
  year?: number | string | null;
}

const GROUP_ORDER: M6Group[] = [
  'HISPANIC',
  'AMERICAN_INDIAN',
  'ASIAN',
  'BLACK',
  'NATIVE_HAWAIIAN',
  'WHITE',
];

const GROUP_SHORT_LABELS: Record<M6Group, string> = {
  HISPANIC: 'Hispanic',
  AMERICAN_INDIAN: 'Am. Indian',
  ASIAN: 'Asian',
  BLACK: 'Black',
  NATIVE_HAWAIIAN: 'Nat. Hawaiian',
  WHITE: 'White',
};

function formatPct(pct: number): string {
  return `${(pct * 100).toFixed(2)}%`;
}

function formatCount(n: number): string {
  return new Intl.NumberFormat('en-US').format(n);
}

/**
 * Color rule per Michael P. (Wallace v2):
 *   - Red   : application% < population% (under-representation)
 *   - Green : application% > population%
 *   - Black : equal (or both zero)
 */
function pctColorClass(cell: M6GroupCell): string {
  if (cell.populationPct === 0 && cell.applicationPct === 0) return 'text-ink-3';
  if (cell.applicationPct < cell.populationPct) return 'text-red-600 font-semibold';
  if (cell.applicationPct > cell.populationPct) return 'text-green-600 font-semibold';
  return 'text-ink-3';
}

export function M6Matrix({ rows, year }: M6MatrixProps) {
  if (rows.length === 0) {
    return (
      <div className="rounded border border-dashed border-rule p-8 text-center">
        <p className="text-sm text-ink-3">No Assessment Areas configured.</p>
        <Link
          to="/cra/assessment-areas"
          className="mt-3 inline-flex items-center gap-1 text-sm text-teal-600 hover:underline"
        >
          Build an Assessment Area <ArrowRightIcon className="h-3.5 w-3.5" />
        </Link>
      </div>
    );
  }

  // Michael Wallace's Redlining template (2026-10-18): two bands — Minority Population by Assessment
  // Area, then HMDA Applications by Assessment Area — over five minority groups (no White column).
  // Same layout as the Word export (fldM6Table.ts).
  const bandGroups = GROUP_ORDER.filter(g => g !== 'WHITE');
  const thBase = 'px-3 py-2 text-right';
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full border-collapse text-sm">
        <thead>
          <tr className="bg-panel-2 text-xs font-medium uppercase tracking-wide text-ink-3">
            <th rowSpan={2} className="sticky left-0 z-10 bg-panel-2 px-3 py-2 text-left align-bottom">Assessment Area</th>
            <th colSpan={bandGroups.length + 1} className="px-3 py-2 text-center border-l border-rule">Minority Population by Assessment Area</th>
            <th colSpan={bandGroups.length + 1} className="px-3 py-2 text-center border-l border-rule">
              {year ? `${year} ` : ''}HMDA Applications by Assessment Area
            </th>
          </tr>
          <tr className="bg-panel-2 text-xs font-medium uppercase tracking-wide text-ink-3">
            <th className={clsx(thBase, 'border-l border-rule')}>Total Pop.</th>
            {bandGroups.map(g => <th key={`p-${g}`} className={thBase}>{GROUP_SHORT_LABELS[g]}</th>)}
            <th className={clsx(thBase, 'border-l border-rule')}>Total Apps</th>
            {bandGroups.map(g => <th key={`a-${g}`} className={thBase}>{GROUP_SHORT_LABELS[g]}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const groupsByKey = Object.fromEntries(
              row.groups.map(g => [g.group, g]),
            ) as Record<M6Group, M6GroupCell>;

            return (
              <tr key={row.assessmentAreaName} className="border-b border-rule hover:bg-panel-2">
                <td className="sticky left-0 z-10 bg-panel px-3 py-3 font-medium text-ink">
                  {row.assessmentAreaName}
                </td>
                <td className="px-3 py-3 text-right tabular-nums text-ink-2 border-l border-rule">
                  {formatCount(row.totalPopulation)}
                </td>
                {bandGroups.map(group => {
                  const cell = groupsByKey[group];
                  return (
                    <td key={`p-${group}`} className="px-3 py-3 text-right tabular-nums text-ink-3">
                      {cell ? formatPct(cell.populationPct) : '—'}
                    </td>
                  );
                })}
                <td className="px-3 py-3 text-right tabular-nums text-ink-2 border-l border-rule">
                  {formatCount(row.totalApplications)}
                </td>
                {bandGroups.map(group => {
                  const cell = groupsByKey[group];
                  return (
                    <td key={`a-${group}`} className={clsx('px-3 py-3 text-right tabular-nums', cell ? pctColorClass(cell) : 'text-ink-4')}>
                      {cell ? `${formatCount(cell.applicationCount)} / ${formatPct(cell.applicationPct)}` : '—'}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="mt-3 flex flex-wrap gap-4 text-xs text-ink-3">
        <span>
          <span className="mr-1 inline-block h-2 w-2 rounded-full bg-red-500" />
          Under-represented (app% &lt; pop%)
        </span>
        <span>
          <span className="mr-1 inline-block h-2 w-2 rounded-full bg-green-500" />
          Over-represented
        </span>
        <span>
          <span className="mr-1 inline-block h-2 w-2 rounded-full bg-ink-4" />
          Equal / no population
        </span>
      </div>
    </div>
  );
}
